import React, { useCallback, useEffect, useMemo } from 'react';
import IconNext from '@/assets/IconRight.svg';

import useModal from '@/hooks/useModal';
import useApi from '@/hooks/useApi';

import { getTerms } from '@/services/requests/Liferay';
import { TResponseTerms } from '@/services/requests/Liferay/types';
import { TCard } from '@/services/requests/Recommendation/types/template1';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import { isHttpLink } from '@/helpers/StringHelper/isHttpLink';
import { getValue } from '@/helpers/GetValueHelper';

type TProps = {
  data: TCard;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, cardId: string) => void;
  showButton: boolean;
  recommendationId: string;
};

export const Card = React.memo(
  ({ data, onClick, showButton, recommendationId }: TProps) => {
    const { id, icon, title } = data;
    const link = getValue(data, 'cta[0].link', '') as string;

    const modal = useModal();
    const { ApiLiferay } = useApi();

    useEffect(() => {
      modal.setRecommendationId(recommendationId);
    }, []);

    const getData = async (contentKey: string) => {
      let modalTitle = 'Ops!';
      let modalContent = 'Desculpa! Não conseguimos carregar o conteúdo.';

      await getTerms(ApiLiferay, contentKey).then(
        (response: TResponseTerms | undefined) => {
          if (response) {
            modalTitle = response.title;
            modalContent = response.content;
          }
        },
      );

      modal.setContent(modalTitle, modalContent);
    };

    const handlerOpenModal = useCallback(async () => {
      modal.setContent('Carregando...', '');
      modal.setShow(true);
    }, [modal]);

    const handlerHttpLink = async (
      event: React.MouseEvent<HTMLAnchorElement>,
    ) => {
      // Http Link
      if (link && isHttpLink(link)) {
        onClick(event, id);
        window.location.href = link;
      }

      // Liferay Content
      if (link && !isHttpLink(link)) {
        onClick(event, id);
        handlerOpenModal();
        await getData(link);
      }
    };
    const imageBackground = getValue(data, 'image.image.url', '') as string;
    const styleBackgroundImage = useMemo(() => {
      return {
        background: `radial-gradient(58.02% 41.39% at 50% 50%, rgba(0, 0, 0, 0) 0%, rgba(3, 3, 3, 0.24) 100%),
        url(${imageBackground})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      };
    }, [imageBackground]);

    const styleCardLink = useMemo(() => {
      return link ? { cursor: 'pointer' } : { cursor: 'default' };
    }, [link]);

    const formattedLink = useMemo(() => {
      return isHttpLink(link) ? link : undefined;
    }, [link]);

    const formattedTitle = useMemo(() => {
      if (!showButton) {
        return title;
      }
      return `${title} <button><img src="${IconNext}" /></button>`;
    }, [showButton, title]);

    return (
      <a
        href={formattedLink}
        title={clearHtmlTags(title)}
        className="vitrine-template1__card"
        key={id}
        data-id={id}
        onClick={handlerHttpLink}
        style={{ ...styleBackgroundImage, ...styleCardLink }}
      >
        <img src={icon} alt={clearHtmlTags(title)} />
        <span
          className="vitrine-template1__card--link"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: formattedTitle }}
        />
      </a>
    );
  },
);
